import React, { useState } from "react";
import { useInView } from "react-intersection-observer";

const faqs = [
  {
    q: "Is the Zero Typing installer safe to download on my Windows PC?",
    a: "Yes. Every build we ship is signed under a verified publisher, delivered over an encrypted HTTPS connection, and scanned clean by antivirus before release. That means Windows SmartScreen lets it through on fresh devices without the \"unknown publisher\" warning, and Defender won't quarantine it. If your browser ever flags a copy of Zero Typing, it didn't come from us, so always download from this page.",
  },
  {
    q: "How do I install Zero Typing, and how long does setup actually take?",
    a: "Download the .msi, double-click it, and follow the setup wizard. Accept the license, pick an install location or keep the default, then click Install. The whole thing takes under a minute on a typical machine. Zero Typing launches automatically when setup finishes, and you can start dictating as soon as you sign in. No restart, no extra drivers, no command line.",
  },
  {
    q: "Which versions of Windows does Zero Typing support?",
    a: "Zero Typing runs on Windows 10 (64 bit) and Windows 11, on both Intel and AMD processors. Any PC that comfortably runs a modern browser has more than enough headroom for it. We don't currently support 32 bit Windows or older releases like Windows 7 and 8.1, since those no longer receive security updates from Microsoft.",
  },
  {
    q: "Does Zero Typing need microphone access, and what happens to my voice data?",
    a: "Yes. Voice typing needs your microphone, so Windows will show a permission prompt the first time you dictate. Grant it once and you're set. Zero Typing only listens while you're actively dictating, so it isn't recording in the background, and you can revoke access at any time from Windows Settings, under Privacy & security, then Microphone.",
  },
  {
    q: "Can I cancel my plan whenever I want, or am I locked into a contract?",
    a: "You can cancel any time, right from your account settings. There are no contracts, no cancellation fees, and no need to email anyone to ask permission. Your plan stays active through the end of the period you've already paid for, and you keep full access until then. If you change your mind later, you can resubscribe and pick up where you left off.",
  },
  {
    q: "What should I do if the install fails or something goes wrong?",
    a: "Start by making sure you're on a supported version of Windows and that the download finished completely, then try running the installer again. If it still won't go through, reach out through the Help page with a short description of what you're seeing. A screenshot of the error helps a lot. Our team replies within a few hours on business days and will walk you through it.",
  },
];

const FAQItem = ({ q, a, open, onToggle, animate, delay }) => (
  <div
    style={{ animationDelay: `${delay}ms` }}
    className={`${
      animate ? "animate-slideUp z-[3] anim-preserve" : "opacity-0 z-0"
    } rounded-2xl border border-[#0000000D] transition-all ${
      open ? "bg-[#F3F8FD]" : "bg-[#F9F9F9]"
    }`}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left"
    >
      <span className="font-inter font-semibold text-[#0F1B2D] text-[0.95rem] sm:text-[1rem] leading-[1.45]">
        {q}
      </span>
      <span className="text-[#33425A] text-xl font-light shrink-0 leading-[1.45]">
        {open ? "×" : "+"}
      </span>
    </button>
    {open && (
      <div className="px-5 pb-4 font-inter text-[#33425A] text-[0.875rem] sm:text-[0.9375rem] leading-[1.55]">
        {a}
      </div>
    )}
  </div>
);

const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState(-1);
  const { ref: headRef, inView: headView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });
  const { ref: listRef, inView: listView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section className="w-full max-w-[820px] mx-auto md:px-7 px-4 flex flex-col gap-8">
      <h2
        ref={headRef}
        className={`${
          headView ? "animate-slideUp z-[3] anim-preserve" : "opacity-0 z-0"
        } font-jakarta font-extrabold text-[#0F1B2D] text-center text-[2.625rem] md:text-[2.4rem] lg:text-[2.8rem] xl:text-[3.125rem] leading-[1.24] tracking-[-0.02em]`}
      >
        Questions,{" "}
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3AB1F6] to-[#407BFF]">
          Answered.
        </span>
      </h2>

      <div
        ref={listRef}
        className="flex flex-col gap-3"
      >
        {faqs.map((f, i) => (
          <FAQItem
            key={i}
            q={f.q}
            a={f.a}
            open={openIdx === i}
            onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            animate={listView}
            delay={i * 100}
          />
        ))}
      </div>
    </section>
  );
};

export default FAQSection;
